class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const details = cartDrawerNote.closest('details');
    const panel = details?.querySelector('.cart-drawer__note-panel');
    if (panel?.id) {
      cartDrawerNote.setAttribute('aria-controls', panel.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.preventDefault();
      this.toggleCartNote(details, panel, cartDrawerNote);
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  toggleCartNote(details, panel, summary) {
    if (!details || !panel || this.noteAnimating) return;

    const opening = !details.open;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      details.open = opening;
      summary.setAttribute('aria-expanded', String(opening));
      return;
    }

    this.noteAnimating = true;
    const duration = opening ? 420 : 320;

    const finish = () => {
      if (!this.noteAnimating) return;
      this.noteAnimating = false;
      panel.style.height = '';
      panel.style.overflow = '';
      panel.style.transition = '';
      if (!opening) details.open = false;
    };

    const onEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;
      panel.removeEventListener('transitionend', onEnd);
      finish();
    };

    panel.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, duration + 80);

    if (opening) {
      details.open = true;
      summary.setAttribute('aria-expanded', 'true');
      panel.style.overflow = 'hidden';
      panel.style.height = '0px';
      panel.offsetHeight;
      panel.style.transition = `height ${duration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
      panel.style.height = `${panel.scrollHeight}px`;
    } else {
      summary.setAttribute('aria-expanded', 'false');
      panel.style.overflow = 'hidden';
      panel.style.height = `${panel.scrollHeight}px`;
      panel.offsetHeight;
      panel.style.transition = `height ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      panel.style.height = '0px';
    }
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
